/**
 * kanban/KanbanColumn.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * One vertical lane of the Kanban board.
 *
 * Responsibilities:
 *  - Render the column header (tinted label · accent bar · themed count badge)
 *  - Render the "+" add button (emits onAddTask(columnKey))
 *  - Act as the drop zone — delegates all drag events to useKanbanDrag via props
 *  - Render a list of <KanbanCard> components
 *  - Show an empty-state placeholder when the column has no cards
 *  - Visual highlight when a card is being dragged over this column
 *
 * No business logic — purely presentational + event forwarding.
 */

import React from 'react';
import { PlusOutlined } from '@ant-design/icons';
import KanbanCard from './KanbanCard';

/**
 * @param {object}      column        - one entry from KANBAN_COLUMNS
 * @param {object[]}    tasks         - tasks belonging to this column
 * @param {boolean}     isDropTarget  - true when a dragged card is hovering here
 * @param {string|null} draggedTaskId - _id of the card currently being dragged
 * @param {Function}    onDragOver    - (e, columnKey) => void
 * @param {Function}    onDragLeave   - (e) => void
 * @param {Function}    onDrop        - (e, columnKey) => void
 * @param {Function}    onDragStart   - (e, task) => void  — passed through to cards
 * @param {Function}    onDragEnd     - (e) => void         — passed through to cards
 * @param {Function}    onCardClick   - (task) => void      — opens detail drawer
 * @param {Function}    onAddTask     - (columnKey) => void — opens create-task modal
 */
export default function KanbanColumn({
    column,
    tasks = [],
    isDropTarget = false,
    draggedTaskId = null,
    onDragOver,
    onDragLeave,
    onDrop,
    onDragStart,
    onDragEnd,
    onCardClick,
    onAddTask,
}) {
    const {
        key,
        label,
        accentColor,
        labelClass,
        badgeBg,
        badgeText,
        emptyMessage,
    } = column;

    // In Progress uses the brand gradient for its badge — handled inline
    const isBrandGradient = badgeBg === '__brand_gradient__';

    return (
        <div
            data-tour={`kanban-col-${key}`}
            className="flex flex-col flex-shrink-0"
            style={{ minWidth: 288, width: 288 }}
        >
            {/* ── Column header ──────────────────────────────────────────── */}
            <div className="flex items-center justify-between px-1 mb-3">
                <div className="flex items-center gap-2">
                    {/* Tinted column label */}
                    <span className={`text-xs font-extrabold uppercase tracking-widest ${labelClass}`}>
                        {label}
                    </span>

                    {/* Task-count badge — themed per column */}
                    {isBrandGradient ? (
                        <span
                            className={`flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full ${badgeText}`}
                            style={{ background: 'linear-gradient(90deg, #03488A 0%, #00AAF1 100%)' }}
                        >
                            {String(tasks.length).padStart(2, '0')}
                        </span>
                    ) : (
                        <span className={`flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full ${badgeBg} ${badgeText}`}>
                            {tasks.length}
                        </span>
                    )}
                </div>

                {/* Add card button */}
                <button
                    data-tour="kanban-add-card-btn"
                    onClick={() => onAddTask?.(key)}
                    title={`Add task to ${label}`}
                    className="p-1 rounded-md hover:bg-[#e7e8e9] dark:hover:bg-[#334155] text-[#424750] dark:text-slate-400 transition-colors"
                    aria-label={`Add task to ${label}`}
                >
                    <PlusOutlined style={{ fontSize: 14 }} />
                </button>
            </div>

            {/* Thin accent bar under the header */}
            <div
                className="h-0.5 rounded-full mb-3 opacity-70"
                style={{ backgroundColor: accentColor }}
                aria-hidden="true"
            />

            {/* ── Drop zone + cards ───────────────────────────────────────── */}
            <div
                onDragOver={(e) => onDragOver(e, key)}
                onDragLeave={onDragLeave}
                onDrop={(e) => onDrop(e, key)}
                className={`
                    flex-1 flex flex-col gap-3
                    h-[calc(100vh-320px)] min-h-[500px] max-h-[750px]
                    overflow-y-auto overflow-x-hidden
                    pr-1.5
                    rounded-xl p-3
                    transition-all duration-200
                    [&::-webkit-scrollbar]:w-1.5
                    [&::-webkit-scrollbar-track]:bg-transparent
                    [&::-webkit-scrollbar-thumb]:bg-slate-300
                    [&::-webkit-scrollbar-thumb]:rounded-full
                    dark:[&::-webkit-scrollbar-thumb]:bg-slate-700
                    hover:[&::-webkit-scrollbar-thumb]:bg-slate-400
                    ${isDropTarget
                        ? 'bg-[#e7f5ff] dark:bg-[#1a2e4a] ring-2 ring-[#28b5fd]/50 ring-inset'
                        : 'bg-[#f3f4f5]/60 dark:bg-[#1e293b]/40'
                    }
                `}
            >
                {tasks.length === 0 ? (
                    <div
                        className={`
                            flex flex-col items-center justify-center flex-1
                            rounded-lg border-2 border-dashed
                            py-10 px-4 text-center
                            transition-colors duration-200
                            ${isDropTarget
                                ? 'border-[#28b5fd]/50 bg-[#e7f5ff]/50 dark:bg-[#1a2e4a]/50'
                                : 'border-[#e7e8e9] dark:border-slate-700'
                            }
                        `}
                    >
                        <span
                            className="material-symbols-outlined text-3xl mb-2 text-[#c2c6d2] dark:text-slate-600"
                            style={{ fontFamily: 'Material Symbols Outlined' }}
                            aria-hidden="true"
                        >
                            {isDropTarget ? 'add_circle' : 'inbox'}
                        </span>
                        <p className="text-xs font-medium text-[#c2c6d2] dark:text-slate-600">
                            {isDropTarget ? 'Drop here' : emptyMessage}
                        </p>
                    </div>
                ) : (
                    tasks.map((task) => (
                        <KanbanCard
                            key={task._id}
                            task={task}
                            column={column}
                            isDragging={draggedTaskId === task._id}
                            onDragStart={onDragStart}
                            onDragEnd={onDragEnd}
                            onCardClick={onCardClick}
                        />
                    ))
                )}
            </div>
        </div>
    );
}
